import { ref, computed, watch } from "vue";
import { debounce } from "@chatfood/core-utils";
import type { ISatSelectProps } from "@chatfood/design-system";
import { useDate } from "@chatfood/design-system";
import type { ISchedulePickerEmits } from "./emits";
import type { ISchedulePickerProps } from "./props";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import { useDirectBackend } from "#imports";
import type { IDomainOutletTimeSlots } from "~/domain/outlet-time-slots";

type ISelectOptions = ISatSelectProps["options"][0];

export const useSchedulePicker = (
  props: ISchedulePickerProps,
  // @ts-expect-error there is no way to type defineEmits so far
  emits: ReturnType<typeof defineEmits<ISchedulePickerEmits>>,
) => {
  const isLoading = ref(false);
  const acceptsAsap = ref(false);
  const schedules = ref<IDomainOutletTimeSlots["schedules"]>([]);
  const selectedDay = ref<ISelectOptions>();
  const selectedTimeSlot = ref<ISelectOptions>();

  const { outletRepo } = useDirectBackend();

  const { getReadableDate, formatTimeRange } = useDate();

  const setSelectedDefaults = () => {
    if (!props.value) {
      selectedDay.value = availableDays.value[0];
      selectedTimeSlot.value = availableTimeSlots.value[0];
      return;
    }

    const { day, time } = props.value;

    selectedDay.value =
      availableDays.value.find((opt) => opt.id === day) ??
      availableDays.value[0];

    selectedTimeSlot.value =
      availableTimeSlots.value.find((opt) => opt.id.startsWith(time)) ??
      availableTimeSlots.value[0];
  };

  const emitSelectedDate = debounce(() => {
    if (!selectedDay.value || !selectedTimeSlot.value) {
      return;
    }

    emits("select", {
      day: selectedDay.value.id,
      time: selectedTimeSlot.value.id.split("-")[0],
    });
  }, 300);

  watch(
    [selectedDay, selectedTimeSlot],
    () => {
      emitSelectedDate();
    },
    {
      immediate: true,
    },
  );

  const fetchAvailableSchedules = async (
    outletId: string,
    orderingMode: IDomainOrderingMode,
  ) => {
    isLoading.value = true;

    try {
      const res = await outletRepo.getTimeSlots({
        outletId,
        orderingMode,
        salesChannel: props.salesChannel,
      });

      acceptsAsap.value = res.isAcceptsAsapOrders;
      schedules.value = res.schedules;
      setSelectedDefaults();
    } finally {
      isLoading.value = false;
    }
  };

  const availableDays = computed<ISelectOptions[]>(() => {
    return schedules.value.reduce((acc, cur) => {
      if (cur.slots.length) {
        acc.push({
          id: cur.day,
          name: getReadableDate(cur.day, "ll"),
        });
      }

      return acc;
    }, [] as ISelectOptions[]);
  });

  const availableTimeSlots = computed<ISelectOptions[]>(() => {
    if (!selectedDay.value) return [];

    return (
      schedules.value
        .find((slot) => {
          return slot.day === selectedDay.value?.id;
        })
        ?.slots.map((a) => {
          return { id: a, name: formatTimeRange(a) };
        }) ?? []
    );
  });

  const onSelectDay = (option: ISelectOptions) => {
    if (selectedDay.value?.id === option.id) return;

    selectedDay.value = option;
    selectedTimeSlot.value = availableTimeSlots.value[0];
  };

  const onSelectTimeSlot = (option: ISelectOptions) => {
    selectedTimeSlot.value = option;
  };

  return {
    fetchAvailableSchedules,
    isLoading,
    selectedDay,
    selectedTimeSlot,
    availableDays,
    availableTimeSlots,
    onSelectDay,
    onSelectTimeSlot,
  };
};
